import { cn } from '../../lib/utils';

/**
 * ProgressBar - Lucky Number Design System
 *
 * Shows tickets sold progress for raffles.
 * Petrol gradient by default, "hot" variant when near sold out.
 */
export default function ProgressBar({
  sold,
  total,
  showLabel = true,
  showPercentage = false,
  size = 'md',
  light = false, // For light backgrounds
  compact = false, // Compact mode: just bar + count on same line
  className,
}) {
  const percentage = total > 0 ? Math.round((sold / total) * 100) : 0;
  const isSoldOut = sold >= total;
  const isHot = percentage >= 80;

  const sizes = {
    sm: 'h-1.5',
    md: 'h-2',
    lg: 'h-3',
  };

  // Compact mode: bar with small count
  if (compact) {
    return (
      <div className={cn('flex items-center gap-2', className)}>
        <div
          className={cn(
            'flex-1 rounded-full overflow-hidden',
            sizes[size],
            light ? 'bg-midnight/10' : 'bg-midnight-lighter'
          )}
        >
          <div
            className={cn(
              'h-full rounded-full transition-all duration-500 ease-out',
              isSoldOut && 'bg-error',
              isHot && !isSoldOut && 'bg-gradient-to-r from-warning to-error',
              !isHot && !isSoldOut && 'bg-gradient-to-r from-petrol to-petrol-light'
            )}
            style={{ width: `${Math.min(percentage, 100)}%` }}
          />
        </div>
        <span className={cn(
          'text-2xs font-mono whitespace-nowrap',
          light ? 'text-midnight/50' : 'text-cream/50'
        )}>
          {sold}/{total}
        </span>
      </div>
    );
  }

  return (
    <div className={className}>
      {/* Label */}
      {showLabel && (
        <div className="flex justify-between items-center mb-1.5">
          <span className={cn(
            'text-xs',
            light ? 'text-midnight/60' : 'text-cream/60'
          )}>
            {isSoldOut ? (
              <span className="text-error font-bold">Sold Out</span>
            ) : (
              <>
                <span className={cn(
                  'font-mono font-bold',
                  light ? 'text-midnight' : 'text-cream'
                )}>
                  {sold}
                </span>
                <span className={cn('font-mono', light ? 'text-midnight/40' : 'text-cream/40')}>/{total}</span>
                <span className="ml-1">sold</span>
              </>
            )}
          </span>

          {showPercentage && !isSoldOut && (
            <span className={cn(
              'text-xs font-bold',
              light ? 'text-midnight/70' : 'text-cream/70'
            )}>
              {percentage}%
            </span>
          )}
        </div>
      )}

      {/* Progress bar */}
      <div
        className={cn(
          'w-full rounded-full overflow-hidden',
          sizes[size],
          light ? 'bg-midnight/10' : 'bg-midnight-lighter'
        )}
      >
        <div
          className={cn(
            'h-full rounded-full transition-all duration-500 ease-out',
            isSoldOut && 'bg-error',
            isHot && !isSoldOut && 'bg-gradient-to-r from-warning to-error',
            !isHot && !isSoldOut && 'bg-gradient-to-r from-petrol to-petrol-light'
          )}
          style={{ width: `${Math.min(percentage, 100)}%` }}
        />
      </div>
    </div>
  );
}

/**
 * ThresholdProgressBar
 *
 * Progress bar with minimum threshold indicator.
 */
export function ThresholdProgressBar({
  sold,
  total,
  threshold = 50,
  light = false,
  className,
}) {
  const soldPercentage = total > 0 ? Math.round((sold / total) * 100) : 0;
  const thresholdMet = soldPercentage >= threshold;
  const isSoldOut = sold >= total;

  return (
    <div className={className}>
      {/* Label */}
      <div className="flex justify-between items-center mb-1.5">
        <span className={cn(
          'text-xs',
          light ? 'text-midnight/60' : 'text-cream/60'
        )}>
          {isSoldOut ? (
            <span className="text-error font-bold">Sold Out</span>
          ) : (
            <>
              <span className={cn(
                'font-mono font-bold',
                light ? 'text-midnight' : 'text-cream'
              )}>
                {sold}
              </span>
              <span className={cn('font-mono', light ? 'text-midnight/40' : 'text-cream/40')}>/{total}</span>
            </>
          )}
        </span>

        <span
          className={cn(
            'text-xs font-bold',
            thresholdMet ? 'text-success' : (light ? 'text-midnight/50' : 'text-cream/50')
          )}
        >
          {thresholdMet ? 'Threshold met' : `${threshold}% needed`}
        </span>
      </div>

      {/* Progress bar with threshold marker */}
      <div className="relative">
        <div className={cn(
          'w-full h-2 rounded-full overflow-hidden',
          light ? 'bg-midnight/10' : 'bg-midnight-lighter'
        )}>
          <div
            className={cn(
              'h-full rounded-full transition-all duration-500 ease-out',
              thresholdMet
                ? 'bg-gradient-to-r from-success to-success-dark'
                : 'bg-gradient-to-r from-warning to-warning-dark'
            )}
            style={{ width: `${Math.min(soldPercentage, 100)}%` }}
          />
        </div>

        {/* Threshold marker */}
        <div
          className={cn(
            'absolute top-0 w-0.5 h-2',
            light ? 'bg-midnight/40' : 'bg-cream/40'
          )}
          style={{ left: `${threshold}%` }}
        />
      </div>
    </div>
  );
}

/**
 * OddsProgressBar
 *
 * Shows user's odds in a visual format.
 */
export function OddsProgressBar({
  userTickets,
  totalSold,
  light = false,
  className,
}) {
  const oddsPercentage = totalSold > 0 ? Math.round((userTickets / totalSold) * 100) : 0;

  return (
    <div className={className}>
      <div className="flex justify-between items-center mb-1.5">
        <span className={cn(
          'text-xs',
          light ? 'text-midnight/60' : 'text-cream/60'
        )}>
          Your odds
        </span>
        <span className={cn(
          'text-xs font-mono font-bold',
          light ? 'text-petrol' : 'text-accent'
        )}>
          {userTickets}/{totalSold} ({oddsPercentage}%)
        </span>
      </div>

      <div className={cn(
        'w-full h-2 rounded-full overflow-hidden',
        light ? 'bg-midnight/10' : 'bg-midnight-lighter'
      )}>
        <div
          className={cn(
            'h-full rounded-full transition-all duration-500 ease-out',
            light
              ? 'bg-gradient-to-r from-petrol to-petrol-light'
              : 'bg-gradient-to-r from-accent-dark to-accent'
          )}
          style={{ width: `${Math.min(oddsPercentage, 100)}%` }}
        />
      </div>
    </div>
  );
}
